/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';
import {
	GetOpportunityQuery,
	MyOpportunity,
	OpportunityEntity,
} from '../models/AccountEntity';
import { OpportunityBackofficeDTO } from '../models/Opportunities/OpportunityBackofficeDTO';
import { OpportunityLogEntryDTO } from '../models/Opportunities/OpportunityLogEntryDTO';
import { ApiRequestOptions } from '../core/ApiRequestOptions';
import { SignalingDTO } from '../models/Signaling';
import { sleep } from 'src/utils';
import { OpportunityChecklistItemDTO } from '../models/Opportunities/OpportunityChecklistItemDTO';
import { ExternalChecklistDTO } from '../models/Opportunities/ExternalChecklistDTO';

export class SalesforceOpportunityControllerService {
	constructor(public readonly httpRequest: BaseHttpRequest) {}

	public getOpportunities(
		query: GetOpportunityQuery
	): CancelablePromise<{ data: OpportunityEntity[] }> {
		return this.httpRequest.request({
			method: 'GET',
			url: '/register/opportunities',
			query,
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public getOpportunity(
		id: string
	): CancelablePromise<{ data: OpportunityEntity[] }> {
		return this.httpRequest.request({
			method: 'GET',
			url: '/register/opportunities',
			query: { opportunityId: id, skip: 0, limit: 1 },
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public getOpportunityFields(filters?: {
		leadSource: boolean,
		leadSourceFilter?: (item: any) => boolean,
	}
	): CancelablePromise<any> {
		// @ts-ignore
		return this.httpRequest.request({
			method: 'GET',
			url: '/register/opportunities/fields',
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		}).then((res: any) => {
				return (res || []).map(item => {
					if (item.name == 'LeadSource') {
						// @ts-ignore
						return {...item, picklistValues: (item.picklistValues || []).filter((item) => [
							'sito web',
							'form contatti sito web',
							'store',
							'dialogo',
							'contatto personale',
							'database dealer',
						].includes(item?.value?.toLowerCase()))}
					}
					return item;
				})
		});
	}

	public resetOpportunityAlert(id: string, alert?: string): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'PUT',
			url: `/register/opportunities/${id}/backoffice/details?alert=${alert || ""}`,
			body: { opportunityId: id },
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public getOpportunityAttachment(id: string): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'GET',
			url: '/register/opportunities/attachment/' + id,
			headers: {},
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public editOpportunity(id: string, body: any): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'PUT',
			url: '/register/opportunities/' + id,
			body,
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public addOpportunityAttachments(
		id: string,
		a: { key: string; filename: string }[],
		query = {},
	): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'POST',
			url: '/register/opportunities/' + id + '/attachments',
			body: a,
			query,
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public createOpportunity(body: any, query?: ApiRequestOptions['query']): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'POST',
			url: '/register/opportunities',
			query,
			body,
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public duplicateOpportunity(
		id: string,
		copies: number
	): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'POST',
			url: '/register/opportunities/copy_opportunity',
			body: { id, copies },
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public getOpportunityBackoffice(
		id: string
	): CancelablePromise<OpportunityBackofficeDTO> {
		return this.httpRequest.request({
			method: 'GET',
			url: '/register/opportunities/{id}/backoffice',
			path: {
				id: id,
			},
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public addLogMessage(
		id: string,
		message: string,
		area: string
	): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'POST',
			url: '/register/opportunities/{id}/backoffice/message',
			body: { message },
			path: {
				id: id,
			},
			query: {
				area: area,
			},
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public updateLogMessage(
		id: string,
		entry: OpportunityLogEntryDTO
	): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'PUT',
			url: '/register/opportunities/{id}/backoffice/message',
			body: entry,
			path: {
				id: id,
			},
			query: {},
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public changeCreditStatus(
		id: string,
		newStatus: string
	): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'PUT',
			url: '/register/opportunities/{id}/backoffice/status',
			body: newStatus,
			path: {
				id: id,
			},
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public getMyOpportunities(
		query: ApiRequestOptions['query']
	): CancelablePromise<{ data: MyOpportunity[] }> {
		return this.httpRequest.request({
			method: 'GET',
			url: '/register/opportunities/list',
			query,
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public updateBackofficeDetails(
		id: string,
		iban?: string | null,
		complete?: boolean | null,
	): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'PUT',
			url: '/register/opportunities/{id}/backoffice/details',
			body: {},
			query: { iban, complete },
			path: { id },
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public updateBackofficeChecklistDetails(
		id: string,
		payload: any = {},
	): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'PUT',
			url: '/register/opportunities/{id}/backoffice/details',
			body: payload,
			query: { },
			path: { id },
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public downloadAllDocuments(
		id: string
	): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'GET',
			url: '/register/opportunities/{id}/attachments',
			path: { id },
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public removeOpportunityAttachment(id: string, reason?: string ): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'DELETE',
			url: '/register/opportunities/attachment/{id}',
			path: { id },
			query: { reason },
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public async getMyReports(
		query: ApiRequestOptions['query']
		// @ts-ignore
	): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'GET',
			url: '/register/signaling',
			query,
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public createNewReport(body: any): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'POST',
			url: '/register/signaling',
			body,
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public requestDocumentation(id: string, externalLoading: boolean = true): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'PUT',
			url: `/register/opportunities/${id}/backoffice/request_documents`,
			body: {},
			query: { externalLoading },
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public addRequestDocument(id: string, dto: OpportunityChecklistItemDTO): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'PUT',
			url: `/register/opportunities/${id}/backoffice/add_document`,
			body: dto,
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public getDocumentChecklist(opportunityId: string): CancelablePromise<ExternalChecklistDTO> {
		return this.httpRequest.request({
			method: 'GET',
			url: `/register/opportunities/${opportunityId}/backoffice/checklist`,
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}

	public rejectDocument(id: string, dto: OpportunityChecklistItemDTO): CancelablePromise<any> {
		return this.httpRequest.request({
			method: 'PUT',
			url: `/register/opportunities/${id}/backoffice/reject_document`,
			body: dto,
			errors: {
				401: `Not Authorized`,
				403: `Not Allowed`,
			},
		});
	}
}
